import { createSlice, nanoid } from '@reduxjs/toolkit';
import { isSameOffer, isSameRoomStay } from './quoteRollup';
const initialState = { items: [] };
// A quote line always holds at least one room; fractional quantities are floored.
const clampQuantity = (quantity) => Math.max(1, Math.floor(quantity));
// Rooms staged for an offer can never exceed the room type's availability, and
// adding to a line never drops it below what is already staged.
const cappedQuantity = (current, requested, available) => Math.max(current, Math.min(current + requested, available));
const quoteSlice = createSlice({
    name: 'quote',
    initialState,
    reducers: {
        // Adding an offer already in the quote increments that line rather than
        // duplicating it. The cap is the room type's availability shared across all
        // its rate plans — one Studio available, staged on the flexible rate, leaves
        // no room to add the same Studio on the prepaid rate.
        addLineItem: {
            reducer: (state, action) => {
                const incoming = action.payload;
                const existing = state.items.find(item => isSameOffer(item, incoming));
                // Rooms of this room type already staged on other rate plans; they draw
                // on the same inventory, so they eat into what this line can still hold.
                const otherRoomUnits = state.items
                    .filter(item => item !== existing && isSameRoomStay(item, incoming))
                    .reduce((sum, item) => sum + item.quantity, 0);
                const availableToLine = incoming.availableUnits - otherRoomUnits;
                if (existing) {
                    existing.quantity = cappedQuantity(existing.quantity, incoming.quantity, availableToLine);
                    return;
                }
                const quantity = cappedQuantity(0, incoming.quantity, availableToLine);
                if (quantity >= 1) {
                    state.items.push({ ...incoming, quantity });
                }
            },
            // Generate the id here so the reducer stays a pure function of its input.
            prepare: (draft, quantity) => ({
                payload: { ...draft, id: nanoid(), quantity: clampQuantity(quantity) }
            })
        },
        removeLineItem: (state, action) => {
            state.items = state.items.filter(item => item.id !== action.payload);
        },
        updateQuantity: (state, action) => {
            const item = state.items.find(i => i.id === action.payload.id);
            if (item) {
                // Other rate plans of the same room type share the inventory, so the
                // cap for this line is availability minus what they already hold.
                const otherRoomUnits = state.items
                    .filter(other => other !== item && isSameRoomStay(other, item))
                    .reduce((sum, other) => sum + other.quantity, 0);
                item.quantity = Math.min(clampQuantity(action.payload.quantity), item.availableUnits - otherRoomUnits);
            }
        }
    }
});
export const { addLineItem, removeLineItem, updateQuantity } = quoteSlice.actions;
export const quoteReducer = quoteSlice.reducer;
